import Phaser from 'phaser';
import {
  DIFFICULTY,
  ENEMY,
  DIFFICULTY_PRESETS,
  type DifficultyPresetKey,
  type DifficultyPreset,
} from '../config/GameConfig';

/**
 * Difficulty —— 难度阶梯管理器
 *
 * 每 DIFFICULTY.tierDuration × tierDurMul 毫秒推进一档。
 * 各模块（Spawner / Enemy）通过它的 getter 拿到"当前应当使用"的数值。
 */
export default class Difficulty {
  scene: Phaser.Scene;
  tier = 0;
  /** 升档回调，由 MainScene 注入 */
  onTierUp: ((tier: number) => void) | null = null;

  private _elapsed = 0;
  private _paused = false;
  private _preset: DifficultyPreset;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const presetKey = (scene.registry.get('difficultyPreset') as DifficultyPresetKey) || 'normal';
    this._preset = DIFFICULTY_PRESETS[presetKey] ?? DIFFICULTY_PRESETS.normal;
  }

  update(_time: number, delta: number): void {
    if (this._paused) return;
    this._elapsed += delta;
    // 前 N 波每波时长不同（rampTierDurMul 拉长开局，让玩家有时间打基础）
    // 用累积阈值算 targetTier：从 tier 0 起累加每个 tier 的 duration 到 elapsed 不再够
    let acc = 0;
    let target = 0;
    while (true) {
      const dur = this._tierDurationOf(target);
      if (this._elapsed < acc + dur) break;
      acc += dur;
      target += 1;
    }
    while (this.tier < target) {
      this.tier += 1;
      this.onTierUp?.(this.tier);
    }
  }

  /** 第 t 个 tier 的实际持续 ms（含预设倍率 + ramp 倍率） */
  private _tierDurationOf(t: number): number {
    const ramp = t < DIFFICULTY.rampTiers ? DIFFICULTY.rampTierDurMul[t] : 1;
    return DIFFICULTY.tierDuration * this._preset.tierDurMul * ramp;
  }

  /** 当前 tier 的 ramp 倍率（>= rampTiers 时全部为 1） */
  private _rampHpMul(): number {
    return this.tier < DIFFICULTY.rampTiers ? DIFFICULTY.rampHpMul[this.tier] : 1;
  }
  private _rampSpeedMul(): number {
    return this.tier < DIFFICULTY.rampTiers ? DIFFICULTY.rampSpeedMul[this.tier] : 1;
  }
  private _rampIntervalMul(): number {
    return this.tier < DIFFICULTY.rampTiers ? DIFFICULTY.rampIntervalMul[this.tier] : 1;
  }

  pause(): void { this._paused = true; }
  resume(): void { this._paused = false; }

  get enemyHp(): number {
    const base = ENEMY.baseHp * Math.pow(DIFFICULTY.hpMul, this.tier);
    return Math.max(1, Math.round(base * this._preset.hpMul * this._rampHpMul()));
  }

  get fallSpeed(): number {
    const v = DIFFICULTY.baseSpeed * Math.pow(DIFFICULTY.speedMul, this.tier);
    return Math.min(DIFFICULTY.maxSpeed, Math.round(v * this._preset.speedMul * this._rampSpeedMul()));
  }

  get spawnInterval(): number {
    const v = DIFFICULTY.initialInterval * Math.pow(DIFFICULTY.intervalMul, this.tier);
    return Math.max(DIFFICULTY.minInterval, Math.round(v * this._preset.intervalMul * this._rampIntervalMul()));
  }

  get chestRate(): number {
    const v = DIFFICULTY.initialChestRate - DIFFICULTY.chestRateDecay * this.tier;
    return Math.max(DIFFICULTY.minChestRate, v);
  }

  get msToNextTier(): number {
    // 累加到当前 tier+1 的 duration 边界
    let acc = 0;
    for (let t = 0; t <= this.tier; t++) acc += this._tierDurationOf(t);
    return Math.max(0, acc - this._elapsed);
  }

  get tierProgress(): number {
    let acc = 0;
    for (let t = 0; t < this.tier; t++) acc += this._tierDurationOf(t);
    const within = this._elapsed - acc;
    return Math.min(1, within / this._tierDurationOf(this.tier));
  }
}
